/**
 * @description SEO 组合函数，用于管理页面 SEO 元数据
 * @returns {Object} SEO 辅助函数
 */
export const useSeo = () => {
  const config = useRuntimeConfig()
  const route = useRoute()

  const SEO_TITLE_SUFFIX = '腾达(Tenda) 官方网站'

  const defaultSeo = {
    title: SEO_TITLE_SUFFIX,
    description:
      '腾达（Tenda）官方网站和在线商城，提供 WiFi 路由器、交换机、网络摄像机等全系列网络设备与解决方案。',
    keywords: '腾达, Tenda, 交换机, AP, 路由器, 网桥, 网络解决方案',
    siteName: '腾达(Tenda) 官方网站',
    locale: 'zh_CN',
    type: 'website',
  }

  /**
   * @description 设置页面 SEO 元数据
   * @param {Object} options - SEO 选项
   * @param {string} options.title - 页面标题（不含站点后缀）
   * @param {string} options.description - 页面描述
   * @param {string} options.keywords - 页面关键词
   * @param {string} options.image - 社交分享图片 URL
   * @param {string} options.type - 页面类型
   */
  const setSeo = (options = {}) => {
    const { title, description, keywords, image, type = defaultSeo.type } = options

    const fullTitle = title ? `${title} - ${SEO_TITLE_SUFFIX}` : defaultSeo.title

    useSeoMeta({
      title: fullTitle,
      description: description || defaultSeo.description,
      keywords: keywords || defaultSeo.keywords,

      ogTitle: fullTitle,
      ogDescription: description || defaultSeo.description,
      ogImage: image || `${config.public.apiBase}/og-default.png`,
      ogType: type,
      ogSiteName: defaultSeo.siteName,
      ogLocale: defaultSeo.locale,

      twitterCard: 'summary_large_image',
      twitterTitle: fullTitle,
      twitterDescription: description || defaultSeo.description,
      twitterImage: image || `${config.public.apiBase}/og-default.png`,
    })
  }

  /**
   * @description 设置产品页面 SEO
   * @param {Object} product - 产品数据
   */
  const setProductSeo = (product) => {
    setSeo({
      title: product.name,
      description: product.description || product.summary,
      keywords: `${product.name}, ${product.modelNo}, Tenda`,
      image: product.image,
      type: 'product',
    })
  }

  /**
   * @description 设置解决方案页面 SEO
   * @param {Object} solution - 解决方案数据
   */
  const setSolutionSeo = (solution) => {
    setSeo({
      title: solution.title,
      description: solution.subLabel || solution.title,
      keywords: `${solution.title}, ${solution.labelList?.join(', ')}, Tenda 解决方案`,
      image: solution.mainImage,
      type: 'article',
    })
  }

  /**
   * @description 设置文章/博客页面 SEO
   * @param {Object} article - 文章数据
   */
  const setArticleSeo = (article) => {
    setSeo({
      title: article.title,
      description: article.excerpt || article.summary,
      image: article.coverImage,
      type: 'article',
    })
  }

  /**
   * @description 生成结构化数据 (JSON-LD)
   * @param {string} type - Schema 类型
   * @param {Object} data - Schema 数据
   * @returns {Object} JSON-LD 对象
   */
  const generateStructuredData = (type, data = {}) => {
    const baseData = {
      '@context': 'https://schema.org',
      '@type': type,
      url: `${config.public.apiBase}${route.fullPath}`,
    }

    const schemas = {
      Organization: {
        ...baseData,
        name: 'Tenda',
        description: '企业级网络解决方案',
        logo: `${config.public.apiBase}/logo.png`,
        contactPoint: {
          '@type': 'ContactPoint',
          telephone: '+86-xxx-xxxx-xxxx',
          contactType: 'customer service',
        },
      },
      Product: {
        ...baseData,
        name: data.name,
        description: data.description,
        image: data.image,
        brand: {
          '@type': 'Brand',
          name: 'Tenda',
        },
        offers: {
          '@type': 'Offer',
          price: data.price,
          priceCurrency: 'CNY',
          availability: data.inStock ? 'InStock' : 'OutOfStock',
        },
      },
      Article: {
        ...baseData,
        headline: data.title,
        description: data.excerpt,
        image: data.coverImage,
        datePublished: data.publishedAt,
        dateModified: data.updatedAt,
        author: {
          '@type': 'Person',
          name: data.author,
        },
        publisher: {
          '@type': 'Organization',
          name: 'Tenda',
        },
      },
    }

    return schemas[type] || baseData
  }

  /**
   * @description 注入结构化数据到页面头部
   * @param {string} type - Schema 类型
   * @param {Object} data - Schema 数据
   */
  const injectStructuredData = (type, data) => {
    const structuredData = generateStructuredData(type, data)

    useHead({
      script: [
        {
          type: 'application/ld+json',
          children: JSON.stringify(structuredData),
        },
      ],
    })
  }

  /**
   * @description 设置规范 URL
   * @param {string} url - 规范 URL
   */
  const setCanonical = (url) => {
    useHead({
      link: [
        {
          rel: 'canonical',
          href: url || `${config.public.apiBase}${route.fullPath}`,
        },
      ],
    })
  }

  return {
    setSeo,
    setProductSeo,
    setSolutionSeo,
    setArticleSeo,
    generateStructuredData,
    injectStructuredData,
    setCanonical,
  }
}
